import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  FormArray,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdvancedDatepickerComponent } from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { PurchaseOrderItem } from '../purchase-order.config';
import { PurchaseOrderService } from '../services/purchase-order.service';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { PurchaseOrderDetailItem } from '../purchase-order-add/purchase-order-add.config';
import { formatCurrencyVND, formatNumber } from '@shared/utils/date-formatter.util';
import { OrderDetailsDialogComponent } from '../components/order-details-dialog/order-details-dialog.component';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { formatDateWithCurrentTime } from '../../procurement-date.util';
export interface WarehouseDeliveryPlan {
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  expectedArrivalDate: Date | null;
  deliveryMethod: string | null;
}

export interface ConfirmSupplierData {
  items: Array<{
    id: string;
    skuCode: string;
    skuName: string;
    originalQuantity: number;
    actualQuantity: number;
    uom?: string;
  }>;
  warehouseDeliveryPlans: WarehouseDeliveryPlan[];
}

@Component({
  selector: 'app-confirm-vendor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    InputNumberModule,
    ToastModule,
    CardModule,
    AdvancedDatepickerComponent,
    AppButtonComponent,
    OrderDetailsDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './confirm-vendor.component.html',
  styleUrl: './confirm-vendor.component.css',
})
export class ConfirmVendorComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private dateFormatterService = inject(DateFormatterService);
  private purchaseOrderService = inject(PurchaseOrderService);
  private warehouseOptionsService = inject(WarehouseOptionsService);
  private destroy$ = new Subject<void>();

  confirmForm!: FormGroup;
  loading = signal(false);
  minDate: Date = new Date();
  isFormSubmitted = false;
  private skipCanDeactivate = false;
  
  // PO details loaded from API
  poDetails = signal<PurchaseOrderItem | null>(null);
  
  // Order details dialog
  showOrderDetailsDialog = signal(false);
  orderDetailsData = signal<{
    warehouseCode: string;
    warehouseName: string;
    orderItems: PurchaseOrderDetailItem[];
    tableColumns: TableColumn[];
  } | null>(null);
  
  // Warehouse options
  warehouseOptions = signal<Array<{ id: number; code: string; name: string }>>([]);
  warehousesLoaded = signal(false);

  // Items table columns
  itemsTableColumns: TableColumn[] = [
    {
      label: 'purchase_order.add.table.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 120,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 300,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.quantity',
      prop: 'originalQuantity',
      type: 'text',
      width: 80,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.unit_price',
      prop: 'unitPrice',
      type: 'text',
      width: 100,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.tax',
      prop: 'tax',
      type: 'text',
      width: 120,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.total_value',
      prop: 'totalValue',
      type: 'text',
      width: 120,
      align: 'right',
    },
  ];

  // Items data for table
  itemsTableData = signal<PurchaseOrderDetailItem[]>([]);

  ngOnInit(): void {
    this.initializeForm();
    
    // Get PO ID from route params
    const poId = this.route.snapshot.paramMap.get('id');
    if (poId) {
      // Load warehouses and PO details in parallel using forkJoin for better performance
      this.loadDataInParallel(poId);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_order.confirm_vendor.error.load_po_failed'
        ),
      });
      this.router.navigate(['/procurement/purchase-order']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.confirmForm = this.fb.group({
      items: this.fb.array([]),
      warehousePlans: this.fb.array([]),
    });
  }

  /**
   * Load warehouses and PO details in parallel for better performance
   */
  private loadDataInParallel(poId: string): void {
    this.loading.set(true);

    const warehouses$ = this.loadWarehousesObservable();
    const poDetails$ = this.purchaseOrderService.getPurchaseOrderById(poId);

    forkJoin({
      warehouses: warehouses$,
      poDetails: poDetails$,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ warehouses, poDetails }) => {
          // Set warehouses first
          this.warehouseOptions.set(warehouses);
          this.warehousesLoaded.set(true);

          // Set PO details
          if (poDetails) {
            this.poDetails.set(poDetails);
            // Populate form once after both data sources are ready
            this.populateForm();
          }

          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading data:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_order.confirm_vendor.error.load_po_failed'
            ),
          });
          this.router.navigate(['/procurement/purchase-order']);
        },
      });
  }

  /**
   * Load warehouses and return as Observable (for forkJoin)
   */
  private loadWarehousesObservable() {
    return this.warehouseOptionsService
      .getOptionsPage(
        { page: 1, size: 100, searchText: '' },
        { valueKey: 'id', status: true },
      )
      .pipe(
        map((page) =>
          page.options.map((opt) => {
            const parts = opt.label.split(' - ');
            const code = parts[0] || String(opt.value);
            const name = parts.slice(1).join(' - ') || opt.label;
            return {
              id: Number(opt.value),
              code,
              name,
            };
          }),
        ),
        catchError((error) => {
          console.error('Error loading warehouses:', error);
          return [];
        }),
      );
  }

  private loadPurchaseOrderDetails(poId: string): void {
    // This method is kept for backward compatibility but should use loadDataInParallel instead
    this.loadDataInParallel(poId);
  }

  private loadWarehouses(): void {
    // This method is kept for backward compatibility but should use loadDataInParallel instead
    // Use the observable version for parallel loading
    this.loadWarehousesObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (warehouses) => {
          this.warehouseOptions.set(warehouses);
          this.warehousesLoaded.set(true);
        },
        error: () => {
          this.warehousesLoaded.set(false);
        },
      });
  }

  private initializeWarehousePlans(): void {
    const warehousePlansArray = this.confirmForm.get('warehousePlans') as FormArray;
    warehousePlansArray.clear();

    const tableData = this.itemsTableData();
    if (!tableData || tableData.length === 0) return;

    const uniqueWarehouses = new Map<string, { code: string; name: string }>();
    
    tableData.forEach((item: any) => {
      const warehouseCode = item.warehouseCode || '';
      
      if (warehouseCode) {
        if (!uniqueWarehouses.has(warehouseCode)) {
          const warehouse = this.warehouseOptions().find(
            (wh) => wh.code === warehouseCode
          );
          uniqueWarehouses.set(warehouseCode, {
            code: warehouseCode,
            name: warehouse?.name || item.warehouseName || warehouseCode,
          });
        }
      }
    });

    const sortedWarehouses = Array.from(uniqueWarehouses.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    );

    sortedWarehouses.forEach((warehouse) => {
      const planGroup = this.fb.group({
        warehouseId: [this.warehouseOptions().find(wh => wh.code === warehouse.code)?.id || 0],
        warehouseCode: [{ value: warehouse.code, disabled: true }],
        warehouseName: [{ value: warehouse.name, disabled: true }],
        asnCode: [''],
        expectedArrivalDate: [null], // Removed required validation
      });
      warehousePlansArray.push(planGroup);
    });
  }

  private populateForm(): void {
    const po = this.poDetails();
    if (!po) return;

    const itemsArray = this.confirmForm.get('items') as FormArray;
    itemsArray.clear();

    const tableData: any[] = [];
    
    if (po.items && po.items.length > 0) {
      const warehouseMap = new Map<string, string>();
      po.items.forEach((item: any) => {
        const warehouseCode = item.warehouseCode || '';
        
        if (warehouseCode && !warehouseMap.has(warehouseCode)) {
          const warehouse = this.warehouseOptions().find(
            (wh) => wh.code === warehouseCode
          );
          warehouseMap.set(
            warehouseCode,
            warehouse?.name || item.warehouseName || warehouseCode
          );
        }
      });

      const sortedItems = [...po.items].sort((a: any, b: any) => {
        const codeA = a.warehouseCode || '';
        const codeB = b.warehouseCode || '';
        return codeA.localeCompare(codeB);
      });

      sortedItems.forEach((item: any) => {
        const warehouseCode = item.warehouseCode || '';
        const warehouseName = warehouseMap.get(warehouseCode) || 
                             item.warehouseName || 
                             warehouseCode || 
                             'N/A';
        
        const itemGroup = this.fb.group({
          id: [item.id?.toString() || ''],
          skuCode: [{ value: item.skuCode || '', disabled: true }],
          skuName: [{ value: item.skuName || '', disabled: true }],
          originalQuantity: [{ value: item.quantity || 0, disabled: true }],
          actualQuantity: [
            item.quantity || 0,
            [Validators.required, Validators.min(1)],
          ],
          uom: [{ value: item.uom || 'pcs', disabled: true }],
          warehouseCode: [warehouseCode],
          warehouseName: [warehouseName],
        });
        itemsArray.push(itemGroup);

        const originalQty = item.quantity || 0;
        const unitPrice = item.price || 0;
        const taxPercent = this.normalizeTaxPercent(
          item.taxRate ?? item.tax ?? 0
        );
        const subtotal = originalQty * unitPrice;
        const totalValue = subtotal * (1 + taxPercent / 100);
        
        tableData.push({
          id: item.id?.toString() || '',
          skuCode: item.skuCode || '',
          skuName: item.skuName || '',
          spuCode: item.spuCode || '',
          spuName: item.spuName || '',
          quantity: originalQty,
          unit: item.uom || 'pcs',
          unitPrice: unitPrice,
          tax: taxPercent,
          totalValue: totalValue,
          originalQuantity: originalQty,
          actualQuantity: originalQty,
          warehouseCode: warehouseCode,
          warehouseName: warehouseName,
          purchaseRequestItem: item.purchaseRequestItem || item.id, // Store purchaseRequestItem for API
        } as any);
      });
    }

    this.itemsTableData.set(tableData);

    // Initialize warehouse plans immediately after items are set
    // No need for setTimeout since both warehouses and PO data are already loaded
    const warehousePlansArray = this.confirmForm.get('warehousePlans') as FormArray;
    if (warehousePlansArray.length === 0) {
      this.initializeWarehousePlans();
    }
  }

  onItemCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    const currentData = this.itemsTableData();
    const updatedData = currentData.map((item: any) => {
      if (item.id === event.rowData.id) {
        const updatedItem = { ...item };
        
        if (event.prop === 'actualQuantity') {
          updatedItem.actualQuantity = Number(event.value) || 0;
          const itemsArray = this.confirmForm.get('items') as FormArray;
          const itemGroup = itemsArray.controls.find(
            (control) => control.get('id')?.value === item.id
          ) as FormGroup;
          if (itemGroup) {
            itemGroup.patchValue({ actualQuantity: updatedItem.actualQuantity });
          }
        } else if (event.prop === 'unitPrice') {
          updatedItem.unitPrice = Number(event.value) || 0;
        } else if (event.prop === 'tax') {
          // Treat entered value as percent
          updatedItem.tax = Number(event.value) || 0;
        } else {
          updatedItem[event.prop] = event.value;
        }
        
        // Recalculate total value: (quantity * unitPrice) * (1 + tax/100)
        const quantity = updatedItem.actualQuantity || 0;
        const unitPrice = updatedItem.unitPrice || 0;
        const tax = this.normalizeTaxPercent(updatedItem.tax);
        const subtotal = quantity * unitPrice;
        updatedItem.totalValue = subtotal * (1 + tax / 100);
        
        return updatedItem;
      }
      return item;
    });
    this.itemsTableData.set(updatedData);
  }

  get itemsFormArray(): FormArray {
    return this.confirmForm.get('items') as FormArray;
  }

  getItemFormGroup(index: number): FormGroup {
    return this.itemsFormArray.at(index) as FormGroup;
  }

  get warehousePlansFormArray(): FormArray {
    return this.confirmForm.get('warehousePlans') as FormArray;
  }

  getWarehousePlanFormGroup(index: number): FormGroup {
    return this.warehousePlansFormArray.at(index) as FormGroup;
  }

  onCancel(): void {
    this.router.navigate(['/procurement/purchase-order']);
  }

  onSave(): void {
    this.isFormSubmitted = true;
    
    // Removed validation for expectedArrivalDate - it's no longer required

    if (this.confirmForm.invalid) {
      Object.keys(this.confirmForm.controls).forEach((key) => {
        const control = this.confirmForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });

      this.itemsFormArray.controls.forEach((itemControl) => {
        if (itemControl instanceof FormGroup) {
          Object.keys(itemControl.controls).forEach((key) => {
            const control = itemControl.get(key);
            if (control && !control.disabled) {
              control.markAsTouched();
            }
          });
        }
      });

      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_order.confirm_vendor.validation.required_fields'
        ),
      });
      return;
    }

    const warehousePlans = this.confirmForm.getRawValue().warehousePlans;
    
    // Check if there are any warehouse plans
    if (!warehousePlans || warehousePlans.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_order.confirm_vendor.validation.warehouse_fields_required'
        ),
      });
      return;
    }

    const poId = this.route.snapshot.paramMap.get('id');
    if (!poId) return;

    // Map all warehouse plans to notices, including those without delivery date or notice code
    // Format: { realNoticeCode?: string, warehouseCode: string, deliveryDate?: string }
    const warehouseNotices = warehousePlans.map((plan: any) => {
      const notice: any = {
        warehouseCode: plan.warehouseCode, // Required field
      };
      
      // Add realNoticeCode if it has a value (not null, not empty string)
      if (plan.asnCode && plan.asnCode.trim() !== '') {
        notice.realNoticeCode = plan.asnCode.trim();
      }
      
      // Add deliveryDate if it has a value
      const deliveryDate = plan.expectedArrivalDate
        ? formatDateWithCurrentTime(plan.expectedArrivalDate)
        : null;
      if (deliveryDate) {
        notice.deliveryDate = deliveryDate;
      }
      
      return notice;
    });

    // Log payload for debugging
    console.log('Payload being sent:', {
      id: poId,
      warehouseNotices,
      warehouseNoticesCount: warehouseNotices.length,
    });

    const poIdNumber = Number(poId);
    if (isNaN(poIdNumber)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_order.confirm_vendor.error.invalid_id'
        ),
      });
      return;
    }

    this.loading.set(true);

    const payload = {
      id: poIdNumber,
      warehouseNotices,
    };

    this.purchaseOrderService
      .confirmPurchaseOrderByVendor(poIdNumber, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('API Response:', response);
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.confirmForm.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_order.messages.success.confirm_vendor'
            ),
          });
          this.router.navigate(['/procurement/purchase-order']);
        },
        error: (error) => {
          console.error('Error confirming PO by vendor:', error);
          console.error('Error details:', {
            error,
            payload,
            warehouseNotices,
          });
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_order.confirm_vendor.error.failed_to_update'
            ),
          });
        },
      });
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.confirmForm?.dirty ?? false);
  }

  getFieldError(rowIndex: number, field: string): string | null {
    if (field !== 'actualQuantity') return null;
    
    const tableData = this.itemsTableData();
    const row = tableData[rowIndex];
    if (!row) return null;
    
    const originalQty = (row as any).originalQuantity || 0;
    const actualQty = (row as any).actualQuantity || 0;
    
    if (actualQty < originalQty) {
      return 'warning-quantity';
    }
    
    return null;
  }

  formatDate(date: any): string {
    if (!date) return '';
    return this.dateFormatterService.formatDate(date);
  }

  private normalizeTaxPercent(raw: any): number {
    const num = Number(raw);
    if (isNaN(num)) return 0;
    return num > 1 ? num : num * 100;
  }

  onViewOrderDetails(index: number): void {
    const planGroup = this.getWarehousePlanFormGroup(index);
    const warehouseCode = planGroup.get('warehouseCode')?.value;
    
    if (!warehouseCode) return;
    
    // Map warehouse code to name from warehouseOptions
    const warehouse = this.warehouseOptions().find(
      (wh) => wh.code === warehouseCode
    );
    const warehouseName = warehouse?.name || planGroup.get('warehouseName')?.value || warehouseCode;
    
    // Filter items by warehouse code
    const allItems = this.itemsTableData();
    const filtered = allItems.filter((item: any) => item.warehouseCode === warehouseCode);
    
    // Format data for display (convert numbers to formatted strings)
    const formattedItems = filtered.map((item: any) => ({
      ...item,
      originalQuantity: item.originalQuantity != null ? String(item.originalQuantity) : '',
      unitPrice: item.unitPrice != null ? this.formatNumber(item.unitPrice) : '',
      tax: item.tax != null ? `${this.formatNumber(item.tax)}%` : '',
      totalValue: item.totalValue != null ? this.formatCurrency(item.totalValue) : '',
    }));
    
    // Set dialog data and show dialog
    this.orderDetailsData.set({
      warehouseCode,
      warehouseName,
      orderItems: formattedItems,
      tableColumns: this.itemsTableColumns,
    });
    this.showOrderDetailsDialog.set(true);
  }
  
  onOrderDetailsDialogClose(): void {
    this.showOrderDetailsDialog.set(false);
  }

  private formatNumber(value: number | null | undefined): string {
    return formatNumber(value);
  }

  private formatCurrency(value: number | null | undefined): string {
    return formatCurrencyVND(value);
  }


}
